import { Breadcrumbs, Typography, Grid, Box, Link, Container, Card, Button, Tabs, Tab, useTheme } from '@material-ui/core';
import ChevronRightIcon from '../../icons/ChevronRight';
import PlusIcon from '../../icons/Plus';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useMounted from '../../hooks/useMounted';
import { useState, useCallback, useEffect } from 'react';
import { usersApi } from '../../api/usersApi';
import UsersListTable from '../../components/dashboard/users/UsersListTable';
import CreateUserDialog from '../../components/dashboard/users/CreateUserDialog';
import useAuth from '../../hooks/useAuth';
import { alpha } from '@mui/material/styles';
import { lightVeil, darkVeil, toneAlpha, grey } from '../../theme/tones';
import usePermissions from '../../hooks/usePermissions';

const UsersList = () => {
  const mounted = useMounted();
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState('active');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const user = useAuth();
  const { can } = usePermissions();

  const getUsers = useCallback(async () => {
    try {
      const data = await usersApi.getUsers(window.localStorage.getItem('accessToken'));
      if (mounted.current) {
        setUsers(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [mounted]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const handleUserCreated = (newUser) => {
    setUsers([...users, newUser]);
  };

  const handleTabChange = (event, value) => {
    setCurrentTab(value);
  };

  const activeUsers = users.filter((u) => u.id_service !== 999);
  const archivedUsers = users.filter((u) => u.id_service === 999);

  return (
    <>
      <Helmet>
        <title>Tableau de bord: Liste des utilisateurs | Syndays</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? `0 4px 24px ${darkVeil(0.04)}`
                : `0 4px 24px ${darkVeil(0.2)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  flexShrink: 0,
                }}
              >
                <ManageAccountsIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Liste des utilisateurs
                </Typography>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                >
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}
                  >
                    Tableau de bord
                  </Link>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                  >
                    Utilisateurs
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>

            {can('users.manage', user.user.id_service === 0 && user.user.access_level === 0) && (
              <Button
                startIcon={<PlusIcon fontSize="small" />}
                onClick={() => setCreateDialogOpen(true)}
                variant="contained"
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  color: grey(0),
                  boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.25)}`,
                  '&:hover': {
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.secondary.main, 0.35)}`,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                  px: 2.5,
                  py: 1.2,
                }}
              >
                Créer un utilisateur
              </Button>
            )}
          </Box>
          <Box
            sx={{
              mt: 2,
              p: 0.6,
              display: 'inline-block',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              boxShadow: theme.palette.mode === 'light' ? `0 4px 16px ${darkVeil(0.03)}` : `0 4px 16px ${darkVeil(0.15)}`,
            }}
          >
            <Tabs
              onChange={handleTabChange}
              scrollButtons="auto"
              value={currentTab}
              variant="scrollable"
              sx={{
                minHeight: 44,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  minHeight: 40,
                  borderRadius: '11px',
                  mx: 0.3,
                  px: 2.5,
                  color: 'text.secondary',
                  transition: 'all 0.18s',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? toneAlpha('blue', 0.06) : lightVeil(0.04),
                    color: 'secondary.main',
                  },
                  '&.Mui-selected': {
                    color: grey(0),
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                    boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  },
                },
              }}
            >
              <Tab label="Actifs" value="active" />
              <Tab label="Archivés" value="archived" />
            </Tabs>
          </Box>
          {can('users.manage', user.user.id_service === 0) && (
            <Card
              sx={{
                mt: 4,
                borderRadius: '20px',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) => `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.1)}, 0 2px 4px -1px ${alpha(theme.palette.common.black, 0.06)}`,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.background.default} 100%)`,
                overflow: 'hidden',
              }}
            >
              <UsersListTable users={currentTab === 'active' ? activeUsers : archivedUsers} />
            </Card>
          )}
          {!can('users.manage', user.user.id_service === 0) && 'Vous n\'avez pas le droit de voir les utilisateurs'}
        </Container>
      </Box>

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onUserCreated={handleUserCreated}
      />
    </>
  );
};

export default UsersList;
